/**
 * Cite Modal Island
 *
 * Hydrates the Research Hub Cite This affordance:
 *   - Toggle modal open via [data-cite-toggle]
 *   - Close on [data-cite-close], Esc, or backdrop click
 *   - Tab/Shift-Tab focus trap inside the modal
 *   - Per-block copy via [data-cite-copy] (reads pre.textContent)
 *   - Dispatches `cite_modal_opened` and `cite_copied` CustomEvents.
 *     A listener wired in page-head.ts forwards them to GTM/dataLayer.
 *
 * Server pre-renders the modal HTML via `renderResearchCiteModal`; this island
 * is interaction-only.
 */

interface CiteEventPayload extends Record<string, unknown> {
  readonly event: 'cite_modal_opened' | 'cite_copied';
  readonly surface: 'pai_research';
  readonly slug: string;
  readonly format?: string;
  readonly content_type?: string;
}

interface DataLayerWindow extends Window {
  dataLayer?: Array<Record<string, unknown>>;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function pushAnalyticsEvent(payload: CiteEventPayload): void {
  const w = window as DataLayerWindow;
  w.dataLayer = w.dataLayer ?? [];
  w.dataLayer.push(payload);
}

function getFocusable(modal: HTMLElement): HTMLElement[] {
  return Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  );
}

function trapFocus(modal: HTMLElement, e: KeyboardEvent): void {
  if (e.key !== 'Tab') return;
  const focusables = getFocusable(modal);
  if (focusables.length === 0) return;
  const first = focusables[0]!;
  const last = focusables[focusables.length - 1]!;
  const active = document.activeElement as HTMLElement | null;

  if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

function hydrate(root: HTMLElement): void {
  const modal = root.querySelector<HTMLElement>('[data-cite-modal]')
    ?? document.querySelector<HTMLElement>('[data-cite-modal]');
  if (!modal) return;

  const toggleButtons = root.querySelectorAll<HTMLElement>('[data-cite-toggle]');
  const closeButtons = modal.querySelectorAll<HTMLElement>('[data-cite-close]');
  const copyButtons = modal.querySelectorAll<HTMLButtonElement>('[data-cite-copy]');
  // Scope to THIS island's own live region first (the Codex hub renders one
  // modal + live region per essay row); fall back to the article page's global
  // 'cite-live-region', which sits outside the island root there.
  const liveRegion = root.querySelector<HTMLElement>('[aria-live]')
    ?? document.getElementById('cite-live-region');
  const slug = root.getAttribute('data-article-slug') ?? '';
  const contentType = root.getAttribute('data-article-type') ?? '';

  let lastFocus: HTMLElement | null = null;

  function open(): void {
    lastFocus = document.activeElement as HTMLElement | null;
    // Reparent to <body> so position:fixed anchors to the VIEWPORT, not a
    // transformed ancestor. On the hub index the modal lives inside the
    // fade-up rows container, whose `transform` becomes the containing block
    // for fixed elements — which clipped the modal and killed scrolling on
    // tall citations. Moving it to <body> also makes it survive the page's
    // client-side view-init DOM swap. Idempotent (no-op once already on body).
    if (modal!.parentElement !== document.body) document.body.appendChild(modal!);
    modal!.hidden = false;
    document.body.style.overflow = 'hidden';
    const firstClose = modal!.querySelector<HTMLElement>('[data-cite-close]');
    firstClose?.focus();
    pushAnalyticsEvent({ event: 'cite_modal_opened', surface: 'pai_research', slug });
  }

  function close(): void {
    modal!.hidden = true;
    document.body.style.overflow = '';
    lastFocus?.focus();
  }

  toggleButtons.forEach((btn) => btn.addEventListener('click', open));
  closeButtons.forEach((btn) => btn.addEventListener('click', close));

  modal.addEventListener('click', (e) => {
    if (e.target === modal) close();
  });

  document.addEventListener('keydown', (e) => {
    if (modal.hidden) return;
    if (e.key === 'Escape') {
      close();
      return;
    }
    trapFocus(modal, e);
  });

  copyButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const block = btn.closest<HTMLElement>('[data-cite-block]');
      const pre = block?.querySelector('pre');
      if (!pre) return;
      const text = pre.textContent ?? '';
      const format = btn.dataset.citeFormat ?? 'unknown';

      const after = (success: boolean): void => {
        if (!success) return;
        const original = btn.textContent;
        btn.textContent = 'Copied';
        btn.classList.add('text-green-500', 'border-green-500');
        if (liveRegion) liveRegion.textContent = `${format} citation copied to clipboard`;
        setTimeout(() => {
          btn.textContent = original;
          btn.classList.remove('text-green-500', 'border-green-500');
        }, 2000);
        pushAnalyticsEvent({
          event: 'cite_copied',
          surface: 'pai_research',
          slug,
          format,
          content_type: contentType,
        });
      };

      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(
          () => after(true),
          () => after(false)
        );
        return;
      }
      // Fallback: select + execCommand. Best-effort; modern browsers prefer the
      // promise path above.
      try {
        const range = document.createRange();
        range.selectNodeContents(pre);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        const ok = document.execCommand('copy');
        selection?.removeAllRanges();
        after(ok);
      } catch {
        after(false);
      }
    });
  });
}

function mount(): void {
  // One island per Cite affordance: the article page has exactly one; the
  // Codex hub has one per essay row. Hydrate them all — each self-scopes to
  // its own modal + toggle inside its root.
  const roots = document.querySelectorAll<HTMLElement>('[data-cite-island]');
  roots.forEach((root) => hydrate(root));
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
}

export {};
